import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router";
import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  GetSeasonLeaderboard,
  PostSeasonLeaderboard,
  TPostSeasonLeaderboard,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/leaderboard";
import { GetLeagueDivision } from "api/rpc/2024-04/facilityAdmin/league/scoring/division";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import { TLeagueSeasonLeaderboard, TLeagueDivision } from "redux/reducers/models/league";
import { showError, showSuccess } from "redux/actions/ui";
import { Select } from "components/select";
import { valueToString } from "helpers/Helpers";

export default function Leaderboards() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [leaderboards, setLeaderboards] = useState<Array<TLeagueSeasonLeaderboard>>(undefined);
  const [divisions, setDivisions] = useState<Array<TLeagueDivision>>(undefined);

  const {
    state: newLeaderboardState,
    updateModal,
    closeModal,
  } = useModal<TPostSeasonLeaderboard>({
    league_id: Number(leagueId),
    title: "",
    best_scores: 20,
    organization: "individual",
    league_division_id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadLeaderboards(source.token);
    void loadLeagueDivisions(source.token);

    return () => source.cancel();
  }, []);

  async function loadLeaderboards(token?: CancelToken) {
    const leaderboardRes = await GetSeasonLeaderboard({ league_id: leagueId }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (leaderboardRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading leaderboards")));
      return;
    }

    setLeaderboards(leaderboardRes.data);
  }

  async function loadLeagueDivisions(token?: CancelToken) {
    const divisionRes = await GetLeagueDivision({ league_id: leagueId }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (divisionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading divisions")));
      return;
    }

    setDivisions(divisionRes.data);
  }

  async function createLeaderboard() {
    const leaderboardRes = await PostSeasonLeaderboard(newLeaderboardState, true);

    if (leaderboardRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating leaderboards")));
      return;
    }

    dispatch(showSuccess(t("Leaderboard created successfully")));
    void closeModal();
    void loadLeaderboards();
  }

  const primaryAction = {
    content: t("Create"),
    action: () => updateModal({ isOpen: true }),
  };

  return (
    <Page title={t("Leaderboards")} primaryAction={primaryAction}>
      <DataTable
        columns={[
          { label: t("Title") },
          { label: t("Division") },
          { label: t("Organization") },
        ]}
        loading={leaderboards === undefined}
      >
        {leaderboards?.map(leaderboard => (
          <tr
            key={leaderboard.id}
            onClick={() => history.push(`/admin/league/${leagueId}/scoring/leaderboards/${leaderboard.id}`)}
            className="clickable"
          >
            <td>{leaderboard.title}</td>
            <td>{leaderboard.division?.title}</td>
            <td>{valueToString(leaderboard.organization)}</td>
          </tr>
        ))}
      </DataTable>

      <Portal isMounted={newLeaderboardState.isOpen}>
        <Sheet
          open={newLeaderboardState.isOpen}
          title={t("New Leaderboard")}
          onCancel={closeModal}
          onOk={createLeaderboard}
          okText={t("Create")}
          size="small"
          overflow
        >
          <Input
            className="mb-2"
            label={t("Title")}
            value={newLeaderboardState.title}
            onChange={e => updateModal({ title: e.target.value })}
          />
          <Select
            label={t("Division")}
            onChange={(value: number) => updateModal({ league_division_id: value })}
            className="mb-2"
          >
            {divisions?.map(division => (
              <Select.Option key={division.id} value={division.id}>
                {division.title}
              </Select.Option>
            ))}
          </Select>
          <Input
            className="mb-2"
            label={t("Scores to Count")}
            value={newLeaderboardState.best_scores}
            onChange={e => updateModal({ best_scores: Number(e.target.value) })}
            type="number"
          />
          <Select
            label={t("Organization")}
            defaultValue={"individual"}
            onChange={(value: "individual" | "team") => updateModal({ organization: value })}
          >
            <Select.Option value={"individual"}>{t("Individual")}</Select.Option>
            <Select.Option value={"team"}>{t("Team")}</Select.Option>
          </Select>
        </Sheet>
      </Portal>
    </Page>
  );
}
